import React, { useEffect, useRef, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import hygeaLogo from '../../assets/hygea.png';

import { API_BASE } from '../../config/runtime';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [query, setQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const actionsRef = useRef(null);
  const deleteModalRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', branchId: '', contact: ''
  });

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE}/get_users.php`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        setStatus({ type: 'error', message: data.message || 'Failed to load users' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to load users: ${error.message}` });
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_branches.php`);
      const data = await response.json();
      if (data.success) {
        setBranches(data.branches || []);
      } else {
        setStatus({ type: 'error', message: data.message || 'Failed to load branches' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to load branches: ${error.message}` });
    }
  };

  useEffect(() => {
    loadUsers();
    loadBranches();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!selectedUser) return;
      if (showDeleteConfirm && deleteModalRef.current?.contains(event.target)) return;
      const clickedInsideActions = actionsRef.current?.contains(event.target);
      const clickedRow = event.target.closest('[data-user-row]');
      if (!clickedInsideActions && !clickedRow) {
        setSelectedUser(null);
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [selectedUser, showDeleteConfirm]);

  const closeModal = () => {
    setShowAddForm(false);
    setEditingUserId(null);
    setFormData({ name: '', email: '', branchId: '', contact: '' });
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const endpoint = editingUserId ? 'update_user.php' : 'register_user.php';
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        branch_id: Number(formData.branchId),
        contact: formData.contact.trim(),
      };
      if (editingUserId) payload.user_id = Number(editingUserId);

      const response = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to register user' });
        return;
      }
      await loadUsers();
      setStatus({
        type: 'success',
        message: data.message || (editingUserId ? 'User updated successfully' : 'User created successfully'),
      });
      closeModal();
      setSelectedUser(null);
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to ${editingUserId ? 'update' : 'register'} user: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const managerUsers = users.filter((u) => (u.role || '').toLowerCase() === 'branch manager');

  const filteredUsers = managerUsers.filter((u) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.branch || '').toLowerCase().includes(q) ||
      (u.contact || '').toLowerCase().includes(q)
    );
  });

  const requestDeleteUser = (user) => {
    if (!user) return;
    setSelectedUser(user);
    setActionsMenuOpen(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch(`${API_BASE}/delete_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUser.user_id }),
      });
      const data = await response.json();
      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to delete user' });
        return;
      }
      setStatus({ type: 'success', message: data.message || 'User deleted successfully' });
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      setActionsMenuOpen(false);
      await loadUsers();
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to delete user: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const openEditForm = (user) => {
    setEditingUserId(user.user_id);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      branchId: user.branch_id ? String(user.branch_id) : '',
      contact: user.contact || '',
    });
    setShowAddForm(true);
    setActionsMenuOpen(false);
  };

  return (
    <div className="flex flex-col sm:flex-row min-h-screen hygea-app-bg">
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <div className="bg-white shadow-sm px-4 sm:px-8 py-3 sm:py-4 flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">User Management</h1>
          <img src={hygeaLogo} alt="Hygea Wellness Spa" className="h-9 sm:h-12" />
        </div>

        <div className="flex-1 p-4 sm:p-8">
          <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-4 sm:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">

            {/* Status message */}
            {status.message && (
              <div className={`mb-4 sm:mb-5 rounded-xl border px-4 py-3 text-sm ${
                status.type === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}>
                {status.message}
              </div>
            )}

            {/* Toolbar */}
            <div className="mb-5 sm:mb-6 rounded-xl sm:rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] px-3 sm:px-4 py-3 sm:py-4">
              <div className="flex flex-col gap-3 sm:gap-4">

                {/* Search + Actions row */}
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search branch managers..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full rounded-full border border-[#ddcfc4] bg-white px-4 py-2.5 sm:py-3 pl-9 sm:pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#B89F80]"
                    />
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-3 sm:top-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                    </svg>
                  </div>

                  {/* Actions dropdown */}
                  <div className="relative flex-shrink-0" ref={actionsRef}>
                    <button
                      type="button"
                      disabled={!selectedUser}
                      onClick={() => setActionsMenuOpen((prev) => !prev)}
                      className={`rounded-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-semibold shadow-sm ${
                        selectedUser
                          ? 'bg-gradient-to-r from-[#C8947F] to-[#B8826F] text-white'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Actions
                    </button>
                    {actionsMenuOpen && selectedUser && (
                      <div className="absolute right-0 top-12 z-20 w-36 rounded-xl border border-[#e2d4c9] bg-white p-1.5 shadow-xl">
                        <button
                          type="button"
                          onClick={() => openEditForm(selectedUser)}
                          className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-[#f8efe8]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDeleteUser(selectedUser)}
                          className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Count + Register row */}
                <div className="flex items-center justify-between gap-3">
                  <div className="rounded-full border border-[#ddcfc4] bg-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs font-semibold tracking-wide text-[#7f685e]">
                    MANAGERS: {managerUsers.length}
                  </div>
                  <button
                    onClick={() => {
                      setEditingUserId(null);
                      setFormData({ name: '', email: '', branchId: '', contact: '' });
                      setShowAddForm(true);
                    }}
                    className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-gradient-to-r from-[#B66959] to-[#AE6A5F] px-3 sm:px-4 py-2 text-white shadow text-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold whitespace-nowrap">Register User</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Add/Edit Modal */}
            {showAddForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6">
                <div className="w-full max-w-2xl rounded-2xl sm:rounded-3xl border border-[#E7D9CE] bg-gradient-to-b from-[#FFF9F5] to-white p-5 sm:p-7 shadow-2xl max-h-[90vh] overflow-y-auto">
                  <div className="mb-5 sm:mb-6 flex items-start justify-between gap-3">
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#B07B60]">User Registration</p>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                        {editingUserId ? 'Update Manager' : 'Add Manager'}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {editingUserId
                          ? 'Edit the selected branch manager details.'
                          : 'Create an account and send login credentials by email.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-shrink-0 rounded-full border border-[#E8D9CC] bg-white p-2 text-gray-500 transition hover:bg-[#F7EEE8]"
                      aria-label="Close"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
                    {/* 1 col on mobile, 2 cols on md+ */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold tracking-wide text-gray-600">NAME</span>
                        <input
                          type="text"
                          name="name"
                          placeholder="Enter full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-xl border border-[#DBCABC] bg-white px-4 py-2.5 sm:py-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-[#BE8D72] focus:ring-2 focus:ring-[#EED9CC]"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold tracking-wide text-gray-600">EMAIL</span>
                        <input
                          type="email"
                          name="email"
                          placeholder="name@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-xl border border-[#DBCABC] bg-white px-4 py-2.5 sm:py-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-[#BE8D72] focus:ring-2 focus:ring-[#EED9CC]"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold tracking-wide text-gray-600">BRANCH</span>
                        <select
                          name="branchId"
                          value={formData.branchId}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-xl border border-[#DBCABC] bg-white px-4 py-2.5 sm:py-3 text-sm text-gray-800 outline-none transition focus:border-[#BE8D72] focus:ring-2 focus:ring-[#EED9CC]"
                        >
                          <option value="">Select branch</option>
                          {branches.map((branch) => (
                            <option key={branch.branch_id} value={branch.branch_id}>
                              📍{branch.branch_name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold tracking-wide text-gray-600">CONTACT NO.</span>
                        <input
                          type="text"
                          name="contact"
                          placeholder="09XXXXXXXXX"
                          value={formData.contact}
                          onChange={handleInputChange}
                          required
                          className="w-full rounded-xl border border-[#DBCABC] bg-white px-4 py-2.5 sm:py-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-[#BE8D72] focus:ring-2 focus:ring-[#EED9CC]"
                        />
                      </label>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-3 border-t border-[#E7D9CE] pt-4 sm:pt-5">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="w-full sm:w-auto rounded-xl border border-[#D9C8BB] bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-[#F8F1EC]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-[#B56E5B] to-[#A35F53] px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#B56E5B]/25 transition hover:brightness-105 disabled:opacity-60"
                      >
                        {submitting
                          ? (editingUserId ? 'Updating...' : 'Creating...')
                          : (editingUserId ? 'Update User' : 'Create User')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {showDeleteConfirm && selectedUser && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6">
                <div ref={deleteModalRef} className="w-full max-w-md rounded-2xl border border-[#E7D9CE] bg-gradient-to-b from-[#FFF9F5] to-white p-5 sm:p-6 shadow-2xl">
                  <div className="mb-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#B07B60]">Confirm Deletion</p>
                    <h3 className="text-xl font-bold text-gray-800">Delete Branch Manager?</h3>
                  </div>
                  <p className="text-sm leading-6 text-gray-600">
                    Delete <span className="font-semibold text-gray-800">{selectedUser.name}</span>? This cannot be undone.
                  </p>
                  <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                    Only the account credentials will be removed. Branch history and records will be preserved.
                  </p>
                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={closeDeleteConfirm}
                      disabled={submitting}
                      className="w-full sm:w-auto rounded-xl border border-[#D9C8BB] bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-[#F8F1EC] disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteUser}
                      disabled={submitting}
                      className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-[#B56E5B] to-[#A35F53] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#B56E5B]/25 transition hover:brightness-105 disabled:opacity-60"
                    >
                      {submitting ? 'Deleting...' : 'Delete Manager'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Table header */}
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Branch Manager List</h2>
              <span className="text-xs font-medium uppercase tracking-wide text-[#9a7f73]">Active View</span>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto rounded-2xl border border-[#e7d8ce]">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#F5EDE7]">
                    <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700">NAME</th>
                    <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700">EMAIL</th>
                    <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700">BRANCH</th>
                    <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700">CONTACT NO.</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {!loadingUsers && filteredUsers.map((user, index) => (
                    <tr
                      key={user.user_id}
                      data-user-row="true"
                      className={`h-16 border-t border-[#f1e6df] transition-colors hover:bg-[#fff8f3] cursor-pointer ${
                        index % 2 === 0 ? 'bg-white' : 'bg-[#fffdfa]'
                      } ${selectedUser?.user_id === user.user_id ? 'bg-[#f6ece5]' : ''}`}
                      onClick={() => { setSelectedUser(user); setActionsMenuOpen(false); }}
                    >
                      <td className="px-6 py-3 text-sm font-medium text-gray-700">{user.name}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{user.email}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{user.branch}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{user.contact}</td>
                    </tr>
                  ))}
                  {loadingUsers && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">Loading users...</td>
                    </tr>
                  )}
                  {!loadingUsers && filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">No branch managers found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden space-y-3">
              {loadingUsers && (
                <p className="text-center text-sm text-gray-500 py-6">Loading users...</p>
              )}
              {!loadingUsers && filteredUsers.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">No branch managers found.</p>
              )}
              {!loadingUsers && filteredUsers.map((user) => {
                const isSelected = selectedUser?.user_id === user.user_id;
                return (
                  <div
                    key={user.user_id}
                    data-user-row="true"
                    onClick={() => { setSelectedUser(user); setActionsMenuOpen(false); }}
                    className={`rounded-xl border p-4 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-[#C8947F] bg-[#f6ece5]'
                        : 'border-[#f1e6df] bg-white hover:bg-[#fff8f3]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                      {isSelected && (
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditForm(user); }}
                            className="rounded-lg bg-[#f6ebe4] px-2.5 py-1 text-xs font-semibold text-[#8b5b42] hover:bg-[#ecddd3] transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); requestDeleteUser(user); }}
                            className="rounded-lg bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 transition"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1 break-all">{user.email}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f1e6df]">
                      <span className="text-xs text-[#8b5b42] font-medium">{user.branch || '—'}</span>
                      <span className="text-xs text-gray-500">{user.contact || '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
